import { type ReactNode, useEffect } from "react";
import { useSearchParams } from "react-router";
import { FullScreenLoader, useStrictParams } from "shared-ui";
import { useToast } from "@wayflyer/flyui";
import { useAxios } from "auth-sdk";
import { useMutation } from "@tanstack/react-query";
import type { FundingApplicationSchemas } from "api-types";
import { addError } from "@wayflyer/blackbox-fe";

import { FUNDING_APPLICATION_COMPLETE_CONNECTION_SUBMIT_API } from "../constants";
import { useCompleteConnectionProps } from "../../queries/connectors/queries";

type Props = {
  children?: ReactNode;
  onSuccess?: (searchParams: URLSearchParams) => void | Promise<void>;
  onError?: (searchParams: URLSearchParams) => void | Promise<void>;
};

export const ConnectionAuthCallbackHandler = ({ children, onSuccess, onError }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { customerDomain } = useStrictParams<{ customerDomain: string }>();
  const connectionValue = searchParams.get("complete-connection");
  const oauthError = searchParams.get("oauth-error");
  const connectionId = searchParams.get("connection_id");

  const { data: completeConnectionData, isLoading } = useCompleteConnectionProps(customerDomain, {
    // do nothing for codat connectors, handled in the component
    enabled: !!connectionValue && !isCodatConnector(connectionValue),
    throwOnError: true,
  });

  const {
    mutate: processCompleteConnection,
    isPending,
    isSuccess,
  } = useCompleteConnectionMutation({
    onSuccess,
    onError,
  });

  useEffect(() => {
    if (!connectionValue || !completeConnectionData || isPending || isSuccess) {
      // prevent processing of complete connection if:
      //  - the connection value is not present
      //  - the complete connection data is not present
      //  - the mutation is in flight
      //  - the mutation has already been processed
      return;
    }

    const redirectParams = new URLSearchParams();

    redirectParams.set("platform", connectionValue);

    // handle oauth errors
    if (oauthError) {
      redirectParams.set("oauth-error", oauthError);
      setSearchParams(redirectParams);
      onError?.(redirectParams);
      return;
    }

    // do nothing for marketing and analytics connectors, handled in the component
    if (isMarketingAndAnalyticsConnector(connectionValue)) {
      return;
    }

    if (connectionId) {
      redirectParams.set("connection_id", connectionId);
    }

    if (completeConnectionData.firstConnection) {
      redirectParams.set("first-connection", String(completeConnectionData.firstConnection));
    }

    if (completeConnectionData.store || completeConnectionData.firstConnection) {
      // trigger complete connection
      processCompleteConnection({
        payload: completeConnectionData,
        redirectParams,
      });
    } else {
      // update original callback search params with redirect params
      setSearchParams(redirectParams);
    }
  }, [
    connectionValue,
    oauthError,
    connectionId,
    completeConnectionData,
    searchParams,
    setSearchParams,
    processCompleteConnection,
    onSuccess,
    onError,
    isPending,
    isSuccess,
  ]);

  if (isLoading || isPending) {
    return <FullScreenLoader />;
  }

  return children;
};

function useCompleteConnectionMutation({ onSuccess, onError }: Pick<Props, "onSuccess" | "onError">) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { customerDomain } = useStrictParams<{ customerDomain: string }>();
  const toaster = useToast();

  const axiosClient = useAxios();
  return useMutation({
    mutationFn: ({
      payload,
      redirectParams,
    }: {
      payload: FundingApplicationSchemas.CompleteConnectionPropsSchema;
      redirectParams: URLSearchParams;
    }) =>
      axiosClient
        .post(FUNDING_APPLICATION_COMPLETE_CONNECTION_SUBMIT_API.replace(":customerSlug", customerDomain), payload)
        .then(() => {
          const connectionValue = searchParams.get("complete-connection");
          if (connectionValue) {
            redirectParams.set("connectionSucceed", connectionValue);
          }
          setSearchParams(redirectParams);
          onSuccess?.(redirectParams);
        }),
    onError: (error) => {
      addError(error);
      toaster.critical(error.message);
      onError?.(searchParams);
    },
  });
}

function isCodatConnector(connector: string): boolean {
  return ["xero", "netsuite", "quickbooks", "codat_accounting", "codat_file_uploads"].includes(connector);
}

function isMarketingAndAnalyticsConnector(connector: string): boolean {
  return ["facebook2", "google_ads2", "ga", "tiktok"].includes(connector);
}
